import React from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  FormErrorMessage,
} from "@chakra-ui/react";
import { SendIcon } from "@root/assets/icons/SendIcon";
import { useTranslation } from "react-i18next";
import { useInquiryContext } from "@root/context/inquiries";
import { useForm } from "react-hook-form";

type InquiryFormValues = {
  title: string;
  content: string;
};

const InquiriesTab: React.FC = () => {
  const { t } = useTranslation("common");
  const { addInquiry, addInquiryLoading } = useInquiryContext();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InquiryFormValues>();

  const onSubmit = async (data: InquiryFormValues) => {
    await addInquiry(data);
    reset(); // clears the form
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid #E2E8F0"
      p={{ base: 4, md: 8 }}
      mt={2}
      w="100%"
      boxShadow="sm"
    >
      <Heading as="h3" size="md" fontWeight={700} mb={1} color="#23262F">
        {t("inquiriesTab.submitNewInquiry")}
      </Heading>
      <Text color="#5C6670" fontSize="16px" mb={6}>
        {t("inquiriesTab.unresolvedIssue")}
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={!!errors.title} mb={5} isRequired>
          <FormLabel fontWeight={600} fontSize="15px" color="#23262F">
            {t("inquiriesTab.titleLabel")}
          </FormLabel>
          <Input
            placeholder={t("inquiriesTab.titlePlaceholder")}
            {...register("title", {
              required: t("inquiriesTab.titleRequired"),
            })}
            fontSize="16px"
            bg="#F9FAFB"
            borderRadius="8px"
            borderColor="#D1D5DB"
            _focus={{
              borderColor: "#1819B4",
              boxShadow: "0 0 0 1px #1819B4",
            }}
          />
          <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.content} mb={6} isRequired>
          <FormLabel fontWeight={600} fontSize="15px" color="#23262F">
            {t("inquiriesTab.contentLabel")}
          </FormLabel>
          <Textarea
            placeholder={t("inquiriesTab.contentPlaceholder")}
            {...register("content", {
              required: t("inquiriesTab.contentRequired"),
            })}
            fontSize="16px"
            bg="#F9FAFB"
            borderRadius="8px"
            borderColor="#D1D5DB"
            minH="120px"
            _focus={{
              borderColor: "#1819B4",
              boxShadow: "0 0 0 1px #1819B4",
            }}
          />
          <FormErrorMessage>{errors.content?.message}</FormErrorMessage>
        </FormControl>

        <Button
          type="submit"
          data-tour-item="journalist-customer-center-inquiry-submit"
          isLoading={addInquiryLoading || isSubmitting}
          loadingText={t("inquiriesTab.submitting")}
          leftIcon={<SendIcon />}
          bg="#1819B4"
          color="#fff"
          fontWeight={600}
          fontSize="16px"
          borderRadius="8px"
          px={8}
          py={6}
          _hover={{ bg: "#1819B4" }}
          _active={{ bg: "#1819B4" }}
        >
          {t("inquiriesTab.submitButton")}
        </Button>
      </form>
    </Box>
  );
};

export default InquiriesTab;
